// The grid while it is edited: each retail price a Dinero (or null, not sold), turned to and from
// the contract's Money only by parseMoney and formatMoney. A cell is below wholesale when it holds
// less than the reseller pays, compared with dinero.js; the API checks the same again.
import { formatMoney, parseMoney, type Dinero } from "@kilat/contracts/money";
import { PRICE_ACTIONS, type PriceAction, type ResellerTldTerm } from "@kilat/contracts/reseller-pricing";
import { lessThan } from "dinero.js";

/** One offered term while it is edited. */
export type PriceRow = { years: number } & Record<PriceAction, Dinero<number> | null>;

const toAmount = (money: string | null) => (money === null ? null : parseMoney(money));
const toMoney = (amount: Dinero<number> | null) => (amount === null ? null : formatMoney(amount));

/** "1:register": a cell by its row in the grid and its action, as the API's details name it. */
export const cellKey = (index: number, action: string) => `${index}:${action}`;

export const priceRows = (terms: readonly ResellerTldTerm[]): PriceRow[] =>
  terms.map(({ years, retail }) => ({
    years,
    register: toAmount(retail.register),
    transfer: toAmount(retail.transfer),
    renew: toAmount(retail.renew),
  }));

/** The grid as the request body takes it, every offered term in order: still to be parsed by the contract. */
export const toPrices = (rows: readonly PriceRow[]) => ({
  prices: rows.map((row) => ({
    years: row.years,
    register: toMoney(row.register),
    transfer: toMoney(row.transfer),
    renew: toMoney(row.renew),
  })),
});

/** The cells holding less than the reseller pays for them. */
export function belowWholesale(rows: readonly PriceRow[], terms: readonly ResellerTldTerm[]): Set<string> {
  const below = new Set<string>();
  for (const [index, row] of rows.entries()) {
    for (const action of PRICE_ACTIONS) {
      const sold = row[action];
      const paid = terms[index]?.wholesale[action] ?? null;
      if (sold !== null && paid !== null && lessThan(sold, parseMoney(paid))) below.add(cellKey(index, action));
    }
  }
  return below;
}

/** The cells an API refusal names, from details paths like "prices.1.renew". */
export function refusedCells(details: readonly { path?: string | undefined }[]): Set<string> {
  const cells = new Set<string>();
  for (const { path } of details) {
    const match = /^prices\.(\d+)\.(\w+)$/.exec(path ?? "");
    if (match) cells.add(cellKey(Number(match[1]), match[2]!));
  }
  return cells;
}
